<template>
    <div>
        <g-level v-if="!statusIsNone || isActivationArchived" class="margin-bottom-normal">
            <g-level-item>
                <g-button size="small" class="pdf-ignore" @click="$root.go('sponsorships')"> Back</g-button>
            </g-level-item>
            <g-level-item slot="right">
                <brief-status :brief="brief" :show-new="isActivationArchived" style="margin: 0" />
            </g-level-item>
        </g-level>

        <g-alert v-if="statusIsInReview" class="margin-bottom-normal" type="basic">
            <g-row slot="message" type="flex" align="top">
                <g-column :span="2">
                    <g-icon color="secondary" name="exclamation-circle" />
                </g-column>
                <g-column :span="21">
                    <g-typography tag="p" margins> We are currently reviewing your proposal.</g-typography>
                    <g-typography tag="p">
                        If you need to change something,
                        <a @click="handleShowRestartModal"> please delete this proposal and rejoin the campaign.</a>
                    </g-typography>
                </g-column>
            </g-row>
        </g-alert>

        <div class="step-card-wrapper">
            <g-typography tag="h1" font-weight="semibold">
                {{ briefTitle }}
            </g-typography>
            <div v-if="!statusIsNone || isActivationArchived" v-html="$sanitize(brief.activation.description)"></div>
        </div>

        <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
        <div v-if="isBriefAccepted" class="margin-bottom-normal">
            <float-on-mask v-if="showTagsBox" tag="div" :float-up="isBriefAccepted">
                <tags-box :tags="tags" :tag-post="tagPost" :custom-description="tagsOverview" data-testid="tags_box" />
            </float-on-mask>
        </div>

        <div v-if="isBriefAccepted" class="margin-bottom-normal">
            <float-on-mask v-if="showNetworkMarketplaceInvitationBox" tag="div" :float-up="isBriefAccepted">
                <network-marketplace-invitation-box
                    :creator-licensing-configs="brief.creator_licensing_configs"
                    :content-requests="brief.network_content_requests"
                    :latest-ttocm-campaign-history="latestTtocmCampaignHistory"
                />
            </float-on-mask>
        </div>

        <div v-if="isBriefAccepted" class="margin-bottom-normal">
            <float-on-mask v-if="showSelfServeCard" tag="div" :float-up="isBriefAccepted" class="margin-bottom-normal">
                <affiliate-links-card
                    :number-of-links="affiliateLinks.length"
                    :can-create-deep-link="canCreateDeepLink"
                />
            </float-on-mask>
            <float-on-mask
                v-else-if="affiliateLinks.length > 0 && !showSelfServeCard"
                tag="div"
                :float-up="isBriefAccepted"
                class="margin-bottom-normal"
            >
                <affiliate-links-display :affiliate-links="affiliateLinks" />
            </float-on-mask>
            <float-on-mask v-if="displayedDiscountCodes.length > 0" tag="div" :float-up="isBriefAccepted">
                <link-code-box :discount-codes="displayedDiscountCodes" />
            </float-on-mask>
        </div>

        <generic-confirmation-modal
            :visible="showRestartModal"
            confirm-button-text="Delete Proposal"
            :closable="false"
            @cancel="handleRestartCancel"
            @continue="handleRestart"
        >
            <template slot="title"> Delete your proposal and rejoin campaign?</template>

            This will delete your current proposal. This can't be undone.
        </generic-confirmation-modal>
    </div>
</template>

<script>
    import { get } from 'lodash';
    import {
        ACTIVATION_BRIEF_STATUS_IN_REVIEW,
        ACTIVATION_BRIEF_STATUS_NONE
    } from '../../../../constants/activation-brief';
    import ErrorMessageCard from './ErrorMessageCard.vue';
    import ActivationBriefMixin from '../mixins/ActivationBriefMixin';
    import GenericConfirmationModal from '../../activation-briefs/partials/GenericConfirmationModal.vue';
    import BriefStatus from '../../../library/BriefStatus.vue';
    import FloatOnMask from '../../../library/FloatOnMask.vue';
    import NetworkMarketplaceInvitationBox from '../../activation-briefs/partials/NetworkMarketplaceInvitationBox.vue';
    import TagsBox from '../../activation-briefs/partials/TagsBox.vue';
    import { getCustomizableText } from '../../../../helpers/activations/default-texts';
    import ActivationTasksMixin from '../../activation-briefs/mixins/ActivationTasksMixin';
    import LinkCodeBox from './LinkCodeBox.vue';
    import AffiliateLinksCard from './AffiliateLinksCard.vue';
    import AffiliateLinksDisplay from './AffiliateLinksDisplay.vue';
    import useNetworkMarketplace from '../../../../../src/composables/use-network-marketplace';

    export default {
        name: 'OverviewHeader',
        components: {
            AffiliateLinksDisplay,
            ErrorMessageCard,
            GenericConfirmationModal,
            BriefStatus,
            FloatOnMask,
            TagsBox,
            NetworkMarketplaceInvitationBox,
            LinkCodeBox,
            AffiliateLinksCard
        },
        mixins: [ActivationBriefMixin, ActivationTasksMixin],
        props: {
            brief: {
                type: Object,
                required: true
            },
            affiliateLinks: {
                type: Array,
                default: () => []
            },
            discountCodes: {
                type: Array,
                default: () => []
            },
            errorMessage: {
                type: String,
                default: null
            },
            restartBrief: {
                type: Function,
                required: true
            }
        },
        data() {
            return {
                showRestartModal: false
            };
        },
        computed: {
            briefTitle() {
                return get(this, 'brief.activation.public_name', '');
            },
            status() {
                return get(this, 'brief.status', null);
            },
            statusIsNone() {
                return this.status === ACTIVATION_BRIEF_STATUS_NONE;
            },
            statusIsInReview() {
                return this.status === ACTIVATION_BRIEF_STATUS_IN_REVIEW;
            },
            tagsOverview() {
                return getCustomizableText(this.brief, 'live_task_description');
            },
            displayedDiscountCodes() {
                return this.discountCodes.filter((code) => {
                    const codeConfig = this.brief.conversion_configs.find(
                        (config) => config.discount_group_id === code.group_id
                    );

                    return codeConfig ? codeConfig.is_displayed : false;
                });
            },
            canSelfServeAffiliateLinks() {
                const linkConversionConfig = this.brief.conversion_configs.find(
                    // eslint-disable-next-line camelcase
                    ({ brief_id, affiliate_group_id }) => brief_id === this.brief.id && affiliate_group_id !== null
                );
                return linkConversionConfig?.can_self_serve_links || false;
            },
            showSelfServeCard() {
                return this.isBriefAccepted && this.affiliateLinks.length > 0;
            },
            canCreateDeepLink() {
                return this.canSelfServeAffiliateLinks && this.$root.advancedAffiliatePackage;
            },
            showNetworkMarketplaceInvitationBox() {
                return useNetworkMarketplace().showNetworkMarketplaceInvitationBox(this.brief);
            },
            latestTtocmCampaignHistory() {
                return useNetworkMarketplace().getLatestTtocmCampaignHistory(this.brief, this.$root.briefs);
            }
        },
        methods: {
            handleShowRestartModal() {
                this.showRestartModal = true;
            },
            handleRestartCancel() {
                this.showRestartModal = false;
            },
            handleRestart() {
                this.restartBrief();
                this.showRestartModal = false;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .step-card-wrapper {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }
</style>
